<template>
  <Teleport to="body">
    <Transition name="toast-slide">
      <div v-if="isVisible" class="toast-container" :class="[`toast-${type}`, positionClass]">
        <div class="toast-content">
          <div class="toast-icon">
            <svg v-if="type === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" fill="currentColor" fill-opacity="0.2"/>
              <path d="M7 10L9 12L13 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="type === 'error'" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" fill="currentColor" fill-opacity="0.2"/>
              <path d="M12 8L8 12M8 8L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <svg v-else-if="type === 'warning'" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" fill="currentColor" fill-opacity="0.2"/>
              <path d="M10 6V10M10 14H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" fill="currentColor" fill-opacity="0.2"/>
              <path d="M10 6V10M10 14H10.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
          <div class="toast-message">
            <span class="toast-title" v-if="title">{{ title }}</span>
            <span class="toast-text">{{ message }}</span>
          </div>
          <button class="toast-close" @click="close" v-if="closable">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
        </div>
        <div class="toast-progress" v-if="showProgress">
          <div class="toast-progress-bar" :style="{ animationDuration: `${duration}ms` }"></div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, watch, onMounted, onUnmounted } from 'vue';

export default {
  name: 'ToastNotification',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'success',
      validator: (value) => ['success', 'error', 'warning', 'info'].includes(value)
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      required: true
    },
    duration: {
      type: Number,
      default: 3000
    },
    position: {
      type: String,
      default: 'top-right',
      validator: (value) => ['top-right', 'top-left', 'top-center', 'bottom-right', 'bottom-left', 'bottom-center'].includes(value)
    },
    closable: {
      type: Boolean,
      default: true
    },
    showProgress: {
      type: Boolean,
      default: true
    }
  },
  emits: ['update:modelValue', 'close'],
  setup(props, { emit }) {
    const isVisible = ref(props.modelValue);
    let timer = null;

    const positionClass = ref(`toast-${props.position}`);

    const close = () => {
      isVisible.value = false;
      emit('update:modelValue', false);
      emit('close');
    };

    const startTimer = () => {
      if (props.duration > 0) {
        timer = setTimeout(() => {
          close();
        }, props.duration);
      }
    };

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };

    watch(() => props.modelValue, (newVal) => {
      isVisible.value = newVal;
      if (newVal) {
        clearTimer();
        startTimer();
      }
    });

    onMounted(() => {
      if (props.modelValue) {
        startTimer();
      }
    });

    onUnmounted(() => {
      clearTimer();
    });

    return {
      isVisible,
      positionClass,
      close
    };
  }
}
</script>

<style scoped>
.toast-container {
  position: fixed;
  z-index: 9999;
  min-width: 300px;
  max-width: 400px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

/* Position Classes */
.toast-top-right {
  top: 24px;
  right: 24px;
}

.toast-top-left {
  top: 24px;
  left: 24px;
}

.toast-top-center {
  top: 24px;
  left: 50%;
  transform: translateX(-50%);
}

.toast-bottom-right {
  bottom: 24px;
  right: 24px;
}

.toast-bottom-left {
  bottom: 24px;
  left: 24px;
}

.toast-bottom-center {
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
}

.toast-content {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
}

.toast-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-message {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.toast-title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
}

.toast-text {
  font-size: 14px;
  color: #6B7280;
  line-height: 1.4;
}

.toast-close {
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #9CA3AF;
  border-radius: 4px;
  transition: all 0.2s;
}

.toast-close:hover {
  background: #F3F4F6;
  color: #6B7280;
}

.toast-progress {
  height: 3px;
  background: #E5E7EB;
}

.toast-progress-bar {
  height: 100%;
  width: 100%;
  animation: progress-shrink linear forwards;
}

@keyframes progress-shrink {
  from { width: 100%; }
  to { width: 0%; }
}

/* Type Styles */
.toast-success {
  border-left: 4px solid #10B981;
}

.toast-success .toast-icon {
  color: #10B981;
}

.toast-success .toast-progress-bar {
  background: #10B981;
}

.toast-error {
  border-left: 4px solid #EF4444;
}

.toast-error .toast-icon {
  color: #EF4444;
}

.toast-error .toast-progress-bar {
  background: #EF4444;
}

.toast-warning {
  border-left: 4px solid #F59E0B;
}

.toast-warning .toast-icon {
  color: #F59E0B;
}

.toast-warning .toast-progress-bar {
  background: #F59E0B;
}

.toast-info {
  border-left: 4px solid #3B82F6;
}

.toast-info .toast-icon {
  color: #3B82F6;
}

.toast-info .toast-progress-bar {
  background: #3B82F6;
}

/* Transition */
.toast-slide-enter-active,
.toast-slide-leave-active {
  transition: all 0.3s ease;
}

.toast-slide-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-slide-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.toast-top-center.toast-slide-enter-from,
.toast-bottom-center.toast-slide-enter-from {
  transform: translateX(-50%) translateY(-20px);
}

.toast-top-center.toast-slide-leave-to,
.toast-bottom-center.toast-slide-leave-to {
  transform: translateX(-50%) translateY(-20px);
}

.toast-top-left.toast-slide-enter-from,
.toast-bottom-left.toast-slide-enter-from {
  transform: translateX(-100%);
}

.toast-top-left.toast-slide-leave-to,
.toast-bottom-left.toast-slide-leave-to {
  transform: translateX(-100%);
}

/* Mobile Responsive */
@media (max-width: 480px) {
  .toast-container {
    min-width: calc(100% - 32px);
    max-width: calc(100% - 32px);
    left: 16px !important;
    right: 16px !important;
    transform: none !important;
  }

  .toast-top-right,
  .toast-top-left,
  .toast-top-center {
    top: 16px;
  }

  .toast-bottom-right,
  .toast-bottom-left,
  .toast-bottom-center {
    bottom: 16px;
  }
}
</style>
